// lib/clients/index.ts
// Client configuration registry and resolution.
// Async path: tries Supabase first (cached), falls back to file-based config.
// Sync path: reads from cache or file (for contexts that can't be async).

import { parousiasiConfig } from './parousiasi';
import { kaizershopConfig } from './kaizershop';
import { acalightConfig } from './acalight';
import { syfakConfig } from './syfak';
import { kostabodaConfig } from './kostaboda';
import { bazaarConfig } from './bazaar';
import type { ClientConfig } from './types';

// ── File-based registry (fallback) ──
const fileConfigs: Record<string, ClientConfig> = {
  parousiasi: parousiasiConfig,
  kaizershop: kaizershopConfig,
  acalight: acalightConfig,
  syfak: syfakConfig,
  kostaboda: kostabodaConfig,
  bazaar: bazaarConfig,
};

const DEFAULT_CLIENT_ID = 'parousiasi';

// ── In-memory cache (shared by async and sync paths) ──
const CACHE_TTL_MS = 60_000; // 60 seconds
const cache = new Map<string, { config: ClientConfig; expiresAt: number }>();

/**
 * Resolve the client ID from various sources.
 */
function resolveId(clientId?: string): string {
  return clientId || process.env.NEXT_PUBLIC_CLIENT_ID || DEFAULT_CLIENT_ID;
}

/**
 * Async: Get client config from Supabase (cached), with file-based fallback.
 * Use this in API routes and async server components.
 */
export async function getClientConfigAsync(clientId?: string): Promise<ClientConfig> {
  const id = resolveId(clientId);

  // Check cache first
  const cached = cache.get(id);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.config;
  }

  // Try Supabase (dynamic import to avoid bundling server-only code into client)
  try {
    const { fetchClientConfigFromDb } = await import('@/lib/supabase/clientConfigFromDb');
    const dbConfig = await fetchClientConfigFromDb(id);
    if (dbConfig) {
      cache.set(id, { config: dbConfig, expiresAt: Date.now() + CACHE_TTL_MS });
      return dbConfig;
    }
  } catch (err) {
    console.warn(`Supabase config fetch failed for "${id}", falling back to file:`, err);
  }

  // Fall back to file-based config
  const fileConfig = fileConfigs[id];
  if (!fileConfig) {
    throw new Error(`Unknown client: "${id}". Available: ${Object.keys(fileConfigs).join(', ')}`);
  }
  cache.set(id, { config: fileConfig, expiresAt: Date.now() + CACHE_TTL_MS });
  return fileConfig;
}

/**
 * Sync: Get client config from cache or file.
 * Use this in client components (via ClientContext) and module-level code.
 * If the async path has been called recently, this returns the cached Supabase config.
 */
export function getClientConfig(clientId?: string): ClientConfig {
  const id = resolveId(clientId);

  // Return cached config if warm (populated by a prior async call)
  const cached = cache.get(id);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.config;
  }

  // Fall back to file-based config
  const config = fileConfigs[id];
  if (!config) {
    throw new Error(`Unknown client: "${id}". Available: ${Object.keys(fileConfigs).join(', ')}`);
  }
  return config;
}

/**
 * Extract clientId from an incoming API request.
 * Resolution order: ?client= query param → NEXT_PUBLIC_CLIENT_ID env var → default.
 */
export function resolveClientFromRequest(request: Request): string {
  const url = new URL(request.url);
  return url.searchParams.get('client') || process.env.NEXT_PUBLIC_CLIENT_ID || DEFAULT_CLIENT_ID;
}

/**
 * List all available client IDs (useful for admin/dashboard).
 */
export function getAvailableClients(): string[] {
  return Object.keys(fileConfigs);
}

// Re-export types for convenience
export type { ClientConfig, FacetFieldConfig, AppliedFilters } from './types';
